import React from 'react';

import { useGraphQLQuery } from '@betterment/graphql';
import { Box, Separator } from '@betterment/samba-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { AccountOwnershipRow } from './components/AccountOwnershipRow';
import {
  AccountOwnershipOptionsDocument,
  AccountOwnershipOptionsQuery,
} from './graphql/AccountOwnershipOptions.generated';
import { UserLegalAccountsDocument } from './graphql/UserLegalAccounts.generated';
import { ownershipOptions } from './ownershipOptions';

import StepHeading from '../../../AccountCreationFlow/components/StepHeading';
import { WorkflowLayout } from '../../../Shared/layout/WorkflowLayout';
import { useSdiAccountStep } from '../SdiAccountFlow';

type AccountOwnershipOption = AccountOwnershipOptionsQuery['goalCreationOption']['accountOwnershipOptions'][number];

export const AccountOwnership: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');

  const { commit } = useSdiAccountStep('ownership');

  const { data: optionsData } = useGraphQLQuery(AccountOwnershipOptionsDocument, {
    throwOnError: true,
    variables: { goalCreationOptionId: 'direct_trading' },
  });

  const { data: accountsData } = useGraphQLQuery(UserLegalAccountsDocument, {
    throwOnError: true,
  });

  const options = optionsData?.goalCreationOption.accountOwnershipOptions;
  const legalAccounts = accountsData?.legalAccounts;

  if (options == null || legalAccounts == null || accountsData == null) {
    return null;
  }

  const hasJointAccount = legalAccounts.some(account => account.ownershipType === 'JOINT');
  const hasTrustAccount = legalAccounts.some(account => account.ownershipType === 'TRUST');
  const hasSdiAccount =
    accountsData.legalSubAccounts?.some(subAccount => subAccount.__typename === 'DirectTradingAccount') ?? false;
  const securityLendingStatus = accountsData.userLevelSettings?.securityLending?.status ?? null;

  const getActionPath = (option: AccountOwnershipOption): string => {
    const config = ownershipOptions[option.name];
    if (config == null) return '';

    const hasExistingAccount =
      (option.name === 'Joint account' && hasJointAccount) || (option.name === 'Existing trust' && hasTrustAccount);

    return config.getPath(hasExistingAccount, hasSdiAccount, securityLendingStatus);
  };

  const handleClick = (option: AccountOwnershipOption) => {
    const config = ownershipOptions[option.name];
    if (config == null) return;

    const existingTrusts =
      config.legalAccountType === 'trust_taxable'
        ? legalAccounts
            .filter(account => account.ownershipType === 'TRUST')
            .map(({ id, displayName }) => ({ id, displayName }))
        : [];
    commit({ ownershipType: config.legalAccountType, existingTrusts });
  };

  return (
    <WorkflowLayout
      pageTitle="Ownership"
      workflowTitle=""
      onBack={() => navigate(-1)}
      onClose={() => (returnTo != null ? navigate(returnTo) : navigate('/'))}
    >
      <Box css={{ marginBottom: 'space.400' }}>
        <StepHeading>Who will own this account?</StepHeading>
      </Box>
      <Box css={{ display: 'grid', gap: 'space.200' }}>
        <Separator />
        {options.map((option, i) => {
          const config = ownershipOptions[option.name];
          return (
            <React.Fragment key={option.id}>
              {i > 0 && <Separator />}
              <AccountOwnershipRow
                option={option}
                icon={config.icon}
                path={getActionPath(option)}
                onClick={() => handleClick(option)}
              />
            </React.Fragment>
          );
        })}
        <Separator />
      </Box>
    </WorkflowLayout>
  );
};
